/**
 * <hero-video-controls>
 *
 * Adds a WCAG 2.2.2 pause/play control for the hero section's autoplaying
 * background video(s). The hero renders muted, looping, autoplay videos with
 * no native controls; this gives keyboard and screen reader users a way to
 * stop the motion. Under prefers-reduced-motion the video starts paused.
 *
 * Expects to wrap a single toggle button ([data-hero-video-toggle]) and to
 * live inside a .hero container that holds one or more <video> elements.
 * Labels are passed in via data-label-play / data-label-pause so the strings
 * stay localizable in Liquid.
 */
class HeroVideoControls extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-hero-video-toggle]');
    const hero = this.closest('.hero');
    this.videos = hero ? Array.from(hero.querySelectorAll('video')) : [];

    if (!this.button || this.videos.length === 0) {
      // Nothing to control; don't present a dead button.
      this.hidden = true;
      return;
    }

    this.labelPlay = this.dataset.labelPlay || 'Play video';
    this.labelPause = this.dataset.labelPause || 'Pause video';

    this.button.addEventListener('click', this.#toggle);

    const prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#setPaused(prefersReducedMotion);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#toggle);
  }

  #toggle = () => {
    const anyPlaying = this.videos.some((video) => !video.paused);
    this.#setPaused(anyPlaying);
  };

  /**
   * @param {boolean} paused
   */
  #setPaused(paused) {
    for (const video of this.videos) {
      if (paused) {
        video.pause();
      } else {
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
      }
    }

    this.button.setAttribute('aria-label', paused ? this.labelPlay : this.labelPause);
    this.button.setAttribute('aria-pressed', paused ? 'true' : 'false');
    this.querySelector('.icon-pause')?.classList.toggle('hidden', paused);
    this.querySelector('.icon-play')?.classList.toggle('hidden', !paused);
  }
}

if (!customElements.get('hero-video-controls')) {
  customElements.define('hero-video-controls', HeroVideoControls);
}
